import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8888'

const Footer: React.FC = () => {
  const [showWaitlist, setShowWaitlist] = useState(false)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const handleWaitlist = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) {return}
    setStatus('loading')
    try {
      const res = await fetch(`${API}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      })
      if (res.ok) {
        setStatus('success')
        setEmail('')
        setTimeout(() => { setShowWaitlist(false); setStatus('idle') }, 2500)
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="footer">
      {/* Waitlist Popup */}
      {showWaitlist && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }} onClick={() => setShowWaitlist(false)}>
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--border-color)',
            borderRadius: 16, padding: '32px', maxWidth: 400, width: '90%'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Join the Waitlist</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
              Get early access to RunAI.live — AI-powered research & work platform.
            </div>
            {status === 'success' ? (
              <div style={{ color: '#10b981', fontSize: 14, textAlign: 'center', padding: '12px 0' }}>
                You&apos;re on the list! We&apos;ll notify you at launch.
              </div>
            ) : (
              <form onSubmit={e => { void handleWaitlist(e); }} style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" required
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--border-color)', background: 'var(--body-bg)',
                    color: 'var(--text-primary)', fontSize: 13, outline: 'none'
                  }}
                />
                <button type="submit" disabled={status === 'loading'} style={{
                  padding: '10px 18px', background: '#3b82f6', color: '#fff',
                  border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600
                }}>
                  {status === 'loading' ? '...' : 'Join'}
                </button>
              </form>
            )}
            {status === 'error' && (
              <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>
                Something went wrong. Try again.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="footer-content">
        <div className="footer-section">
          <div className="footer-logo">RunAI</div>
          <p className="footer-desc">AI research &amp; work collaboration platform</p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '12px', marginBottom: '8px' }}>
            Built on <a href="https://FilizAI.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-blue)', textDecoration: 'none' }}>FilizAI.com</a> infrastructure. RunAI.Live © 2026
          </p>
          <div className="footer-social" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <a href="https://twitter.com/runai" className="social-icon" title="Twitter">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://github.com/runai" className="social-icon" title="GitHub">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
              </svg>
            </a>
            <a href="https://linkedin.com/company/runai" className="social-icon" title="LinkedIn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href="https://discord.gg/runai" className="social-icon" title="Discord">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </a>
          </div>
        </div>
        <div className="footer-section">
          <div className="footer-title">Platform</div>
          <Link to="/research" className="footer-link">Research</Link>
          <Link to="/work" className="footer-link">Work</Link>
          <Link to="/social" className="footer-link">Social</Link>
          <Link to="/aicloud" className="footer-link">AI Cloud</Link>
        </div>
         <div className="footer-section">
           <div className="footer-title">Support & Help</div>
           <Link to="/ask-ai" className="footer-link">Help & Support (AskAI)</Link>
           <Link to="/tokenomics" className="footer-link">Tokenomics</Link>
           <button
             onClick={() => setShowWaitlist(true)}
             style={{
               background: 'none', border: 'none', padding: 0, cursor: 'pointer',
               color: '#3b82f6', fontSize: '13px', textAlign: 'left',
               marginTop: '4px', fontFamily: 'inherit'
             }}
           >
             Join Waitlist →
           </button>
         </div>
      </div>
    </div>
  )
}

export default Footer;
